import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronUp, Check, Info, ShoppingCart, Home } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import { getOrderMedicineUrl, SERVICE_NAME } from './CosmeticSkinCareApi';
import { resolvePdfUrl } from '../../api/Services/resolvePdfUrl';
import WhatsAppShareModal from '../../Shared/WhatsAppShareModal/WhatsAppShareModal';

import heroBannerImage from '../../Assets/Images/rewamp/skin/3. Results/hero banner image.webp';
import downloadIcon from '../../Assets/Images/rewamp/skin/3. Results/section 1/download.svg';
import shareIcon from '../../Assets/Images/rewamp/skin/3. Results/section 1/share.svg';
import analysedIcon from '../../Assets/Images/rewamp/skin/3. Results/section 2/overall skin score card/analysed.svg';
import skinTypeIcon from '../../Assets/Images/rewamp/skin/3. Results/section 2/overall skin score card/skin type.svg';
import airQualityIcon from '../../Assets/Images/rewamp/skin/3. Results/section 3/external stressor/air quality.svg';
import hydrationIcon from '../../Assets/Images/rewamp/skin/3. Results/section 3/external stressor/hydration.svg';
import sleepQualityIcon from '../../Assets/Images/rewamp/skin/3. Results/section 3/external stressor/sleep quality.svg';
import uvIcon from '../../Assets/Images/rewamp/skin/3. Results/section 3/external stressor/uv.svg';
import intelligentActionIcon from '../../Assets/Images/rewamp/skin/3. Results/section 3/intelligent action card/intelligent action insights.svg';
import underlyingFactorsIcon from '../../Assets/Images/rewamp/skin/3. Results/section 3/underlying factors.svg';
import ingredientsHeaderIcon from '../../Assets/Images/rewamp/skin/3. Results/section 4/header icon.svg';
import personalisedHeaderIcon from '../../Assets/Images/rewamp/skin/3. Results/section 5/header icon.svg';
import dietImage from '../../Assets/Images/rewamp/skin/3. Results/diet.webp';
import dietWatermark from '../../Assets/Images/rewamp/skin/3. Results/watermark image in diet card.webp';
import expertPhoto from '../../Assets/Images/rewamp/skin/3. Results/section 6/talk to our skin expert card.svg';

const STEPPER = ['Face Scan', 'Details', 'Analysis', 'Results'];

const BAR_COLORS = ['#E85D4C', '#F0A73D', '#E8CD3D', '#5B8DEF', '#4CAF7D', '#B084F5'];

const FACTOR_ICONS = {
  sleep: sleepQualityIcon,
  water_intake: hydrationIcon,
  sunscreen_usage: uvIcon,
  climate: airQualityIcon,
};

const DIET_TIPS = [
  'Turmeric Milk or Oatmeal Mix',
  'Nuts & Seeds',
  'Watermelon Bowl',
  'Chamomile Tea or Green Tea',
  'Leafy Greens & Citrus Fruits',
];

const EXPERT_POINTS = [
  'Clinical evaluation of concerns',
  'Custom prescription formula',
  '24/7 follow-up support',
];

const humanize = (key) =>
  (key || '').split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const getResultRecord = () => {
  try {
    return JSON.parse(sessionStorage.getItem('cosmeticResultRecord') || 'null');
  } catch {
    return null;
  }
};

const ScoreGauge = ({ score }) => {
  const pct = (Number(score || 0) / 10) * 100;
  const r = 54;
  const circumference = 2 * Math.PI * r;
  const dash = (pct / 100) * circumference;
  return (
    <svg width={140} height={140} viewBox="0 0 140 140">
      <circle cx="70" cy="70" r={r} fill="none" stroke="#DFF1FB" strokeWidth="12" />
      <circle
        cx="70"
        cy="70"
        r={r}
        fill="none"
        stroke="url(#scoreGaugeGrad)"
        strokeWidth="12"
        strokeLinecap="round"
        strokeDasharray={`${dash} ${circumference}`}
        transform="rotate(-90 70 70)"
      />
      <defs>
        <linearGradient id="scoreGaugeGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#004276" />
          <stop offset="100%" stopColor="#004276" />
        </linearGradient>
      </defs>
      <text x="70" y="66" textAnchor="middle" fontSize="26" fontWeight="700" fill="#004276">{score ?? '—'}</text>
      <text x="70" y="84" textAnchor="middle" fontSize="12" fill="#9490A8">/10</text>
    </svg>
  );
};

const Accordion = ({ icon, title, subtitle, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="cr-accordion">
      <button type="button" className="cr-accordion-header" onClick={() => setOpen((o) => !o)}>
        <span className="cr-accordion-header-left">
          <img src={icon} alt="" className="cr-accordion-icon" />
          <span>
            <div className="cr-accordion-title">{title}</div>
            <div className="cr-accordion-subtitle">{subtitle}</div>
          </span>
        </span>
        {open ? <ChevronUp size={18} color="#8B86A8" /> : <ChevronDown size={18} color="#8B86A8" />}
      </button>
      {open && <div className="cr-accordion-body">{children}</div>}
    </div>
  );
};

const CosmeticResults = () => {
  const navigate = useNavigate();
  const [record] = useState(getResultRecord);
  const [whatsappModal, setWhatsappModal] = useState(false);
  const [whatsappLoading, setWhatsappLoading] = useState(false);

  // Results is reached from two different places — the live Analysis flow and
  // the Past Reports list (standalone, or embedded in the AI Reports page) —
  // so "back" must return to wherever the user actually came from. Browser
  // history (navigate(-1)) is unreliable here since it can overshoot past
  // an embedded parent route, so the Past Reports list stashes an explicit
  // return path when opening a report; fall back to history for the live
  // Analysis flow, which doesn't set one.
  const handleBack = () => {
    let returnPath = null;
    try {
      returnPath = sessionStorage.getItem('cosmeticResultsReturnPath');
      // One-time use — otherwise a later visit here via the live Analysis
      // flow would incorrectly inherit this stale return path.
      sessionStorage.removeItem('cosmeticResultsReturnPath');
    } catch {}
    if (returnPath) {
      navigate(returnPath);
    } else {
      // navigate(-1) here would step back through Analysis -> Details ->
      // Face Scan (the whole capture flow, via browser history) instead of
      // just leaving the finished report — go straight to the landing page.
      navigate('/cosmetic-skincare-analysis');
    }
  };
  const handleDownload = async () => {
    if (!record?.PDFREPORT) return;
    const resolvedUrl = await resolvePdfUrl(record.PDFREPORT);
    if (resolvedUrl) window.open(resolvedUrl, '_blank', 'noopener');
    else toast.error('PDF not available');
  };
  const handleShare = () => setWhatsappModal(true);

  const handleWhatsAppShare = async (mobileNumber) => {
    setWhatsappLoading(true);
    try {
      const loginObj = JSON.parse(localStorage.getItem('loginObj') || 'null');
      const patientName =
        record?.PATIENTNAME ||
        loginObj?.name ||
        `${loginObj?.firstName || loginObj?.firstname || ''} ${loginObj?.lastName || loginObj?.lastname || ''}`.trim();

      const resolvedPdfUrl = await resolvePdfUrl(record?.PDFREPORT);
      if (!resolvedPdfUrl) { toast.error('PDF not available'); setWhatsappLoading(false); return; }

      const params = new URLSearchParams({
        patientid: loginObj?.code,
        orderid: record?.ID,
        patient_name: patientName,
        pdf_url: resolvedPdfUrl,
        mobile: mobileNumber,
        service: SERVICE_NAME,
      });
      const response = await fetch(
        `${process.env.REACT_APP_QUANTUMURL}get_pdf_whatsapp?${params.toString()}`,
        { method: 'GET' }
      );
      const responseData = await response.json();
      // Same success signal AiReports uses — the backend status field has a known typo.
      const isSuccess =
        responseData.whatsapp_status === 200 ||
        String(responseData.message).toLowerCase().includes('sent') ||
        String(responseData.status).toLowerCase().includes('success');
      if (isSuccess) {
        toast.success('Report successfully sent to WhatsApp!');
        setWhatsappModal(false);
      } else {
        toast.error(responseData.message || 'Failed to send report');
      }
    } catch {
      toast.error('Error sending report. Please try again.');
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleExploreDiet = () => navigate('/smartdiet');
  const handleBookConsultation = () => {
    // Same destination the Symptom Checker's "Book Consultation" button opens,
    // pre-filtered to Dermatology since that's the relevant specialty for skin scans.
    const today = new Date().toISOString().split('T')[0];
    const params = new URLSearchParams({
      speciality: 'Dermatology',
      date: today,
      consultationType: 'V',
      feeMin: '0',
      feeMax: '2000',
      pageNumber: '0',
      pageSize: '10',
    });
    window.open(`${process.env.REACT_APP_NEW_DOCTOR_URL}doctor-detail?${params.toString()}`, '_blank');
  };

  const detectedConditions = record?.DETECTED_CONDITIONS || [];
  const underlyingFactors = record?.UNDERLYING_FACTORS || [];
  const ingredientMap = record?.INGREDIENT_RECOMMENDATIONS || {};
  const productMap = record?.PRODUCT_RECOMMENDATIONS || {};
  const conditionKeys = Array.from(new Set([...Object.keys(ingredientMap), ...Object.keys(productMap)]));

  if (!record) {
    return (
      <div className="mx-auto w-full">
        <style>{`
          .cr-empty-wrapper {
            min-height: 60vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 40px;
            background-color: #fff; background-image: var(--section-bg-gradient);
          }
          .cr-empty-title { font-weight: 700; font-size: 18px; color: var(--color-brand-primary); margin-bottom: 8px; }
          .cr-empty-desc { font-size: 13px; color: #676C80; margin-bottom: 20px; }
          .cr-empty-btn {
            font-weight: 600; font-size: 14px; color: #ffffff; background: var(--color-brand-primary);
            border: none; border-radius: 999px; padding: 10px 24px; cursor: pointer;
          }
        `}</style>
        <div className="cr-empty-wrapper">
          <div className="cr-empty-title">No results found</div>
          <div className="cr-empty-desc">We couldn't find a completed analysis. Please start a new skin scan.</div>
          <button className="cr-empty-btn" onClick={() => navigate('/cosmetic-skincare-analysis')}>
            Start Cosmetic Analysis
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full">
      <style>{`
        .cr-wrapper {
          background-color: #fff; background-image: var(--section-bg-gradient);
          padding: 24px 40px 64px 40px;
        }

        .cr-header {
          display: grid;
          grid-template-columns: 1fr auto 1fr;
          align-items: center;
          margin-bottom: 24px;
        }

        .cr-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 16px;
          color: #1A1A2E;
          background: none;
          border: none;
          cursor: pointer;
          padding: 0;
          justify-self: start;
        }

        .cr-home-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-weight: 600;
          font-size: 14px;
          color: var(--color-brand-primary);
          background: none;
          border: 1.5px solid #C9EEF8;
          border-radius: 999px;
          padding: 7px 16px;
          cursor: pointer;
          justify-self: end;
        }

        .cr-home-btn:hover {
          background: #EAF3FA;
        }

        .cr-stepper { display: flex; flex-direction: column; align-items: center; gap: 6px; }
        .cr-stepper-caption { font-size: 12px; color: #676C80; }
        .cr-stepper-track { display: flex; align-items: center; }
        .cr-stepper-item { display: flex; flex-direction: column; align-items: center; gap: 4px; width: 90px; }
        .cr-stepper-circle {
          width: 24px; height: 24px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          font-size: 12px; font-weight: 700;
          background: #ffffff; border: 1.5px solid var(--color-brand-primary); color: var(--color-brand-primary);
        }
        .cr-stepper-label { font-size: 12px; font-weight: 600; color: var(--color-brand-primary); }
        .cr-stepper-line { width: 60px; height: 1.5px; background: var(--color-brand-primary); margin-bottom: 20px; }

        .cr-hero {
          display: flex;
          gap: 32px;
          background: #ffffff;
          border-radius: 16px;
          overflow: hidden;
          margin-bottom: 24px;
          flex-wrap: wrap;
        }

        .cr-hero-image {
          flex: 1 1 360px;
          max-width: 560px;
          display: flex;
          align-items: center;
        }

        .cr-hero-image img {
          display: block;
          width: 100%;
          height: auto;
        }

        .cr-hero-content {
          flex: 2 1 360px;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 24px 32px 24px 0;
        }

        .cr-hero-heading {
          font-family: 'Inter', sans-serif;
          font-weight: 700;
          font-size: 28px;
          color: var(--color-brand-primary);
          margin-bottom: 8px;
        }

        .cr-hero-heading-accent { color: var(--color-brand-green); }

        .cr-hero-desc {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          line-height: 1.45;
          color: var(--color-text-subheading);
          margin-bottom: 24px;
          max-width: 400px;
        }

        .cr-hero-actions { display: flex; gap: 12px; flex-wrap: wrap; }

        .cr-hero-actions .cr-btn-outline,
        .cr-hero-actions .cr-btn-primary { min-width: 220px; justify-content: center; padding: 9px 24px; }

        .cr-btn-outline {
          display: inline-flex; align-items: center; gap: 6px;
          font-weight: 600; font-size: 13px;
          background: #ffffff; color: var(--color-brand-primary);
          border: 1px solid var(--color-brand-primary); border-radius: 999px;
          padding: 9px 18px; cursor: pointer;
        }

        .cr-btn-outline:disabled { opacity: 0.5; cursor: not-allowed; }

        .cr-btn-primary {
          display: inline-flex; align-items: center; gap: 6px;
          font-weight: 600; font-size: 13px;
          background: var(--color-brand-primary); color: #ffffff;
          border: none; border-radius: 999px;
          padding: 9px 20px; cursor: pointer;
        }

        .cr-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }

        .cr-score-row {
          display: flex;
          align-items: flex-start;
          gap: 20px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }

        .cr-card {
          background: #ffffff;
          border-radius: 16px;
          padding: 20px;
          border: 1.5px solid #CDEFF8;
          box-shadow: 0px 0px 10px 0px #80DAF140;
        }

        .cr-score-card {
          flex: 1 1 320px;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        }

        .cr-card-title { font-weight: 700; font-size: 14px; color: var(--color-brand-primary); align-self: flex-start; margin-bottom: 8px; }

        .cr-score-condition { font-weight: 700; font-size: 13px; color: var(--color-brand-green); margin-top: 4px; text-transform: capitalize; }

        .cr-score-desc { font-size: 12px; color: #676C80; margin: 10px 0 14px 0; }

        .cr-info-chips { display: flex; gap: 8px; flex-wrap: wrap; justify-content: center; }

        .cr-info-chip {
          display: inline-flex; align-items: center; gap: 5px;
          font-size: 11px; font-weight: 600; color: var(--color-brand-primary);
          background: #F1F8FD; border-radius: 999px; padding: 5px 10px;
        }

        .cr-detail-card { flex: 1 1 380px; }

        .cr-detail-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 14px; }

        .cr-priority-badge {
          font-size: 11px; font-weight: 700; color: var(--color-brand-primary);
          background: #EAF3FA; border-radius: 999px; padding: 4px 10px;
        }

        .cr-bar-row { margin-bottom: 16px; }
        .cr-bar-row:last-child { margin-bottom: 0; }

        .cr-bar-label-row { display: flex; justify-content: space-between; align-items: center; font-size: 12px; font-weight: 600; color: #1A1A2E; margin-bottom: 6px; }

        .cr-bar-track { width: 100%; height: 7px; border-radius: 999px; background: #DFF1FB; }

        .cr-bar-fill { height: 100%; border-radius: 999px; }

        .cr-empty-note { font-size: 12px; color: #9490A8; }

        .cr-accordion {
          background: #ffffff;
          border: 1.5px solid #CDEFF8;
          box-shadow: 0px 0px 10px 0px #80DAF140;
          border-radius: 14px;
          margin-bottom: 16px;
          overflow: hidden;
        }

        .cr-accordion-header {
          width: 100%; display: flex; align-items: center; justify-content: space-between;
          background: none; border: none; padding: 16px 20px; cursor: pointer; font-family: inherit;
        }

        .cr-accordion-header-left { display: flex; align-items: center; gap: 12px; text-align: left; }

        .cr-accordion-icon { width: 44px; height: 44px; flex-shrink: 0; }

        .cr-accordion-title { font-weight: 700; font-size: 14px; color: var(--color-brand-primary); }
        .cr-accordion-subtitle { font-size: 12px; color: #676C80; }
        .cr-accordion-body { padding: 0 20px 20px 20px; }

        .cr-factor-row { display: flex; align-items: flex-start; gap: 10px; margin-bottom: 14px; }
        .cr-factor-row:last-child { margin-bottom: 0; }
        .cr-factor-icon { width: 32px; height: 32px; flex-shrink: 0; }
        .cr-factor-label { font-weight: 600; font-size: 12px; color: #1A1A2E; }
        .cr-factor-desc { font-size: 11px; color: #676C80; }

        .cr-advice-block { margin-bottom: 16px; }
        .cr-advice-block:last-child { margin-bottom: 0; }
        .cr-advice-label { font-weight: 700; font-size: 12px; color: var(--color-brand-green); margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.3px; }
        .cr-advice-text { font-size: 13px; color: #1A1A2E; line-height: 1.5; }

        .cr-condition-group { margin-bottom: 22px; }
        .cr-condition-group:last-child { margin-bottom: 0; }
        .cr-condition-title { font-weight: 700; font-size: 13px; color: var(--color-brand-primary); margin-bottom: 8px; text-transform: capitalize; }

        .cr-ingredient-chips { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 10px; }
        .cr-ingredient-chip {
          font-size: 11px; font-weight: 600; color: var(--color-brand-primary); background: #F1F8FD;
          border-radius: 999px; padding: 4px 10px;
        }

        .cr-product-card {
          border: 1px solid #DFF1FB; border-radius: 10px; padding: 10px 12px; margin-bottom: 8px;
        }
        .cr-product-card:last-child { margin-bottom: 0; }
        .cr-product-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; }
        .cr-product-name { font-weight: 600; font-size: 12px; color: var(--color-brand-primary); text-decoration: underline; text-decoration-color: #80DAF1; }
        .cr-product-link { text-decoration: none; }
        .cr-product-link:hover { color: var(--color-brand-primary); text-decoration: underline; }
        .cr-product-price { font-size: 12px; font-weight: 700; color: var(--color-brand-primary); white-space: nowrap; }
        .cr-product-mrp { font-size: 11px; color: #9490A8; text-decoration: line-through; margin-right: 4px; }
        .cr-product-bottom-row { display: flex; align-items: center; gap: 8px; margin-top: 8px; flex-wrap: wrap; }
        .cr-product-confidence {
          font-size: 10px; font-weight: 700; border-radius: 999px; padding: 2px 8px; display: inline-block;
        }
        .cr-product-confidence.High { background: #E4F9EC; color: #14804A; }
        .cr-product-confidence.Medium { background: #FFF3D9; color: #B8860B; }
        .cr-product-confidence.Low { background: #FDEAEA; color: #C0392B; }
        .cr-product-buy-btn {
          display: inline-flex; align-items: center; gap: 4px;
          font-size: 11px; font-weight: 700; color: #ffffff;
          background: var(--color-brand-primary); border-radius: 999px; padding: 5px 12px;
          text-decoration: none; margin-left: auto; white-space: nowrap;
        }
        .cr-product-buy-btn:hover { background: var(--color-brand-primary-hover); }
        .cr-product-warning { font-size: 11px; color: var(--color-text-subheading); margin-top: 6px; display: flex; gap: 4px; align-items: flex-start; }

        .cr-bottom-row { display: flex; gap: 20px; flex-wrap: wrap; }

        .cr-bottom-card { flex: 1 1 380px; display: flex; flex-direction: column; position: relative; overflow: hidden; }

        .cr-bottom-title { font-weight: 700; font-size: 14px; margin-bottom: 4px; position: relative; z-index: 1; }
        .cr-bottom-desc { font-size: 12px; color: #676C80; margin-bottom: 14px; position: relative; z-index: 1; }

        .cr-diet-watermark {
          position: absolute;
          right: -10px;
          bottom: -10px;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          object-fit: cover;
          opacity: 0.35;
          pointer-events: none;
        }

        .cr-diet-body { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; position: relative; z-index: 1; }

        .cr-diet-list { list-style: disc; padding-left: 18px; font-size: 12px; color: var(--color-text-subheading); flex: 1 1 200px; }
        .cr-diet-list li { margin-bottom: 4px; }

        .cr-diet-visual {
          flex: 1 1 180px;
          position: relative;
          border-radius: 12px;
          background: var(--color-brand-primary);
          color: #ffffff;
          padding: 16px;
          padding-top: 40px;
          text-align: center;
        }

        .cr-diet-visual-photo {
          position: absolute;
          top: -28px;
          left: 50%;
          transform: translateX(-50%);
          width: 64px;
          height: 64px;
          border-radius: 50%;
          object-fit: cover;
          border: 3px solid #ffffff;
          box-shadow: 0px 2px 8px 0px #00000030;
        }

        .cr-diet-visual-text { font-size: 13px; font-weight: 600; margin-bottom: 12px; }

        .cr-btn-white {
          background: #ffffff; color: var(--color-brand-primary); font-weight: 600; font-size: 12px;
          border: none; border-radius: 999px; padding: 8px 16px; cursor: pointer; width: 100%;
        }

        .cr-expert-body { display: flex; gap: 16px; align-items: center; flex-wrap: wrap; position: relative; z-index: 1; }

        .cr-expert-photo {
          width: 64px; height: 64px; border-radius: 50%; flex-shrink: 0;
          object-fit: cover;
        }

        .cr-expert-list { list-style: disc; padding-left: 18px; font-size: 12px; color: var(--color-text-subheading); flex: 1 1 200px; }
        .cr-expert-list li { margin-bottom: 4px; }

        .cr-expert-actions { flex-basis: 100%; margin-top: 14px; }

        @media (max-width: 768px) {
          /* Extra right clearance so content doesn't sit behind the fixed
             "Share Feedback" tab docked at the viewport's right edge. */
          .cr-wrapper { padding: 16px 56px 16px 16px; }
          .cr-header { grid-template-columns: 1fr; gap: 12px; }
          .cr-stepper-track { flex-wrap: nowrap; overflow-x: auto; justify-content: flex-start; -webkit-overflow-scrolling: touch; }
          .cr-stepper-item { width: 62px; flex-shrink: 0; }
          .cr-stepper-line { width: 20px; flex-shrink: 0; }
          .cr-hero { gap: 0; }
          .cr-hero-image { max-width: none; }
          .cr-hero-content { padding: 20px 16px; }
          .cr-hero-actions .cr-btn-outline,
          .cr-hero-actions .cr-btn-primary { min-width: 0; }
          .cr-hero-heading { font-size: 20px; }
          .cr-card { padding: 16px; }
          .cr-accordion-body { padding: 0 16px 16px 16px; }
          /* Stack photo/list/button vertically instead of relying on
             flex-wrap row-packing, which was letting the button share a row
             it didn't actually fit in and get clipped. Also reset every
             child's flex-basis — in the desktop row layout those were sized
             along the width axis, but flex-basis always tracks the main
             axis, which is now vertical (height) here. */
          .cr-diet-body, .cr-expert-body { flex-direction: column; align-items: stretch; }
          .cr-diet-visual, .cr-expert-photo { align-self: center; }
          .cr-expert-list, .cr-diet-list, .cr-expert-actions { flex-basis: auto; }
        }

        @media (max-width: 480px) {
          .cr-wrapper { padding: 12px 52px 12px 12px; }
          .cr-hero-actions .cr-btn-outline,
          .cr-hero-actions .cr-btn-primary { flex: 1 1 auto; justify-content: center; }
          .cr-product-top { flex-direction: column; }
          .cr-product-price { align-self: flex-start; }
        }
      `}</style>

      <div className="cr-wrapper">
        <div className="cr-header">
          <button className="cr-back" onClick={handleBack}>
            <ChevronLeft size={20} />
            Results
          </button>

          <div className="cr-stepper">
            <span className="cr-stepper-caption">Step 4 of 4</span>
            <div className="cr-stepper-track">
              {STEPPER.map((label, i) => (
                <React.Fragment key={label}>
                  {i > 0 && <span className="cr-stepper-line" />}
                  <div className="cr-stepper-item">
                    <span className="cr-stepper-circle"><Check size={13} /></span>
                    <span className="cr-stepper-label">{label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>

          <button className="cr-home-btn" onClick={() => navigate('/')}>
            <Home size={16} />
            Home
          </button>
        </div>

        {/* HERO */}
        <div className="cr-hero">
          <div className="cr-hero-image">
            <img src={heroBannerImage} alt="Your skin analysis" />
          </div>
          <div className="cr-hero-content">
            <h1 className="cr-hero-heading">Your Skin <span className="cr-hero-heading-accent">Analysis Results</span></h1>
            <p className="cr-hero-desc">
              {record.ADVICE_OBSERVATION
                ? record.ADVICE_OBSERVATION
                : `Your primary detected condition is ${humanize(record.FINAL_CONDITION) || 'not specified'}.`}
            </p>
            <div className="cr-hero-actions">
              <button className="cr-btn-outline" onClick={handleDownload} disabled={!record.PDFREPORT}>
                <img src={downloadIcon} alt="" width={14} height={14} />
                {record.PDFREPORT ? 'Download as PDF' : 'Report generating…'}
              </button>
              <button className="cr-btn-primary" onClick={handleShare} disabled={!record.PDFREPORT}>
                <img src={shareIcon} alt="" width={14} height={14} />
                Share
              </button>
            </div>
          </div>
        </div>

        {/* SCORE ROW */}
        <div className="cr-score-row">
          <div className="cr-card cr-score-card">
            <div className="cr-card-title">Overall Skin Score</div>
            <ScoreGauge score={record.OVERALL_SKIN_SCORE} />
            <div className="cr-score-condition">{humanize(record.FINAL_CONDITION) || 'Condition pending'}</div>
            <div className="cr-score-desc">
              {record.ADVICE_SEVERITY || 'Severity details are not available for this scan.'}
            </div>
            <div className="cr-info-chips">
              <span className="cr-info-chip"><img src={skinTypeIcon} alt="" width={12} height={13} />Patient: {record.PATIENTNAME || record.PATIENTID}</span>
              <span className="cr-info-chip"><img src={analysedIcon} alt="" width={12} height={13} />Analyzed: {record.CREATEDDATE ? new Date(record.CREATEDDATE).toLocaleDateString() : '—'}</span>
            </div>
          </div>

          <div className="cr-card cr-detail-card">
            <div className="cr-detail-header">
              <div className="cr-card-title" style={{ marginBottom: 0 }}>Detailed Analysis</div>
              <span className="cr-priority-badge">{detectedConditions.length} Detected</span>
            </div>
            {detectedConditions.length === 0 && <div className="cr-empty-note">No conditions detected in this scan.</div>}
            {detectedConditions.map(({ label, confidence_percent: pct }, i) => (
              <div className="cr-bar-row" key={label}>
                <div className="cr-bar-label-row">
                  <span>{label}</span>
                </div>
                <div className="cr-bar-track">
                  <div className="cr-bar-fill" style={{ width: `${pct != null ? pct : 50}%`, background: BAR_COLORS[i % BAR_COLORS.length] }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ACCORDIONS */}
        <Accordion icon={underlyingFactorsIcon} title="Underlying Factors" subtitle="Possible internal and external factors that may be affecting your skin.">
          <div className="cr-insight-grid">
            {intelligentActionIcon && record.ADVICE_RECOMMENDATION && (
              <div style={{ background: '#004276', color: '#fff', borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <img src={intelligentActionIcon} alt="" width={28} height={28} style={{ marginBottom: 10 }} />
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Intelligent Action Insights</div>
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', margin: 0 }}>{record.ADVICE_RECOMMENDATION}</p>
              </div>
            )}
            {underlyingFactors.length === 0 && <div className="cr-empty-note">No underlying factors reported for this scan.</div>}
            {underlyingFactors.map(({ label, factor, observation }) => (
              <div className="cr-factor-row" key={label}>
                <img src={FACTOR_ICONS[factor] || underlyingFactorsIcon} alt="" className="cr-factor-icon" />
                <div>
                  <div className="cr-factor-label">{label}</div>
                  <div className="cr-factor-desc">{observation}</div>
                </div>
              </div>
            ))}
          </div>
        </Accordion>

        <Accordion icon={ingredientsHeaderIcon} title="Ingredients & Products Recommended For You" subtitle="Clinically proven action to address your specific skin concerns.">
          {conditionKeys.length === 0 && <div className="cr-empty-note">No product recommendations available yet.</div>}
          {conditionKeys.map((key) => (
            <div className="cr-condition-group" key={key}>
              <div className="cr-condition-title">{humanize(key)}</div>
              {ingredientMap[key]?.length > 0 && (
                <div className="cr-ingredient-chips">
                  {ingredientMap[key].map((ing) => (
                    <span className="cr-ingredient-chip" key={ing}>{ing}</span>
                  ))}
                </div>
              )}
              {(productMap[key] || []).map((p) => (
                <div className="cr-product-card" key={p.drug_id}>
                  <div className="cr-product-top">
                    <a
                      className="cr-product-name cr-product-link"
                      href={getOrderMedicineUrl(p.drug_name)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {p.drug_name}
                    </a>
                    <div className="cr-product-price">
                      ₹{p.mrp}
                    </div>
                  </div>
                  <div className="cr-product-bottom-row">
                    {p.confidence && <span className={`cr-product-confidence ${p.confidence}`}>{p.confidence} confidence</span>}
                    <a
                      className="cr-product-buy-btn"
                      href={getOrderMedicineUrl(p.drug_name)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <ShoppingCart size={12} />
                      Buy Now
                    </a>
                  </div>
                  {p.usage_warning && (
                    <div className="cr-product-warning">
                      <Info size={12} style={{ flexShrink: 0, marginTop: 1 }} />
                      {p.usage_warning}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </Accordion>

        <Accordion icon={personalisedHeaderIcon} title="Your Personalised Skin Analysis" subtitle="AI-detected concerns with targeted ingredient recommendations for your skin.">
          <div className="cr-advice-block">
            <div className="cr-advice-label">Observation</div>
            <div className="cr-advice-text">{record.ADVICE_OBSERVATION || 'Not available.'}</div>
          </div>
          <div className="cr-advice-block">
            <div className="cr-advice-label">Severity</div>
            <div className="cr-advice-text">{record.ADVICE_SEVERITY || 'Not available.'}</div>
          </div>
          <div className="cr-advice-block">
            <div className="cr-advice-label">Recommendation</div>
            <div className="cr-advice-text">{record.ADVICE_RECOMMENDATION || 'Not available.'}</div>
          </div>
          <div className="cr-advice-block">
            <div className="cr-advice-label">When to consult a doctor</div>
            <div className="cr-advice-text">{record.ADVICE_WHEN_TO_CONSULT || 'Not available.'}</div>
          </div>
        </Accordion>

        {/* BOTTOM ROW */}
        <div className="cr-bottom-row">
          <div className="cr-card cr-bottom-card">
            <img src={dietWatermark} alt="" className="cr-diet-watermark" />
            <div className="cr-bottom-title" style={{ color: '#00BD7F' }}>Diet recommendation</div>
            <div className="cr-bottom-desc">Anti-inflammatory and Hydrating diet to improve skin elasticity.</div>
            <div className="cr-diet-body">
              <ul className="cr-diet-list">
                {DIET_TIPS.map((tip) => <li key={tip}>{tip}</li>)}
              </ul>
              <div className="cr-diet-visual">
                <img src={dietImage} alt="" className="cr-diet-visual-photo" />
                <div className="cr-diet-visual-text">Support your skin from within</div>
                <button className="cr-btn-white" onClick={handleExploreDiet}>Explore our SmartDiet Solution</button>
              </div>
            </div>
          </div>

          <div className="cr-card cr-bottom-card">
            <div className="cr-bottom-title" style={{ color: '#004276' }}>Talk to our Skin Expert</div>
            <div className="cr-expert-body">
              <img src={expertPhoto} alt="Skin expert" className="cr-expert-photo" />
              <ul className="cr-expert-list">
                {EXPERT_POINTS.map((p) => <li key={p}>{p}</li>)}
              </ul>
              <div className="cr-expert-actions">
                <button className="cr-btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={handleBookConsultation}>
                  Book Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" />
      <WhatsAppShareModal
        isOpen={whatsappModal}
        onClose={() => setWhatsappModal(false)}
        onConfirm={handleWhatsAppShare}
        loading={whatsappLoading}
      />
    </div>
  );
};

export default CosmeticResults;
